// @flow
import { get } from "lodash";
import type { Actions } from "@tvg/shared-actions/BetActions";
import type { BetTicketState } from "@tvg/types/Bet";
import type { TalentPickData } from "@tvg/types/TalentPicks";
import {
  SetStartBettingAction,
  SetRaceOfBet,
  SetBetAmountAction,
  SetSelectionScratchedAction,
  SetBetTypeAction,
  SetBetSelectionsAction,
  SetBetSelectionsMatrixAction,
  ResetBetSelectionsAction,
  ResetBetSelectctionsKeepWinAction,
  ResetBettingAction,
  SetBetStateAction,
  SetGroupBetTicketStatusClearAll,
  SetPickLegAction,
  SetPickAction,
  SetGroupBetAddProcessing,
  SetGroupBetAddSuccess,
  SetGroupBetAddError,
  SetGroupBetRemoveProcessing,
  SetGroupBetRemoveSuccess,
  SetGroupBetRemoveError,
  SetBetSlipOpen
} from "@tvg/desktop-bet/src/store/actions";
import { isURP } from "@tvg/utils/generalUtils";

import type {
  BetGroupStatus,
  SetGroupBetTicketStatus,
  SetGroupBetTicketStatusClear,
  SetGroupBetTicketStop,
  SetGroupBetTicketCountersReset
} from "./actions";

export type State = {
  config: BetTicketState,
  repeatBetNumber: number,
  showRepeatBet: boolean,
  betGroupStatus: BetGroupStatus,
  processingBets: number,
  errorBets: number,
  // URP
  successBets: number,
  betSelections: string[][],
  scratchedSelections: string[][],
  startBetting: boolean,
  betState: "none" | "success" | "error",
  betAmount: number,
  betType: number | null | typeof undefined,
  betLeg: number,
  talentPick: TalentPickData | null | typeof undefined,
  raceOfBet: string,
  betSlipOpen: boolean
};

export type BetConfirmationAction =
  | Actions
  | SetGroupBetTicketStatus
  | SetGroupBetTicketStatusClear
  | SetGroupBetTicketStop
  | SetGroupBetTicketCountersReset
  | SetStartBettingAction
  | SetRaceOfBet
  | SetBetAmountAction
  | SetSelectionScratchedAction
  | SetBetTypeAction
  | SetBetSelectionsAction
  | SetBetSelectionsMatrixAction
  | ResetBetSelectionsAction
  | ResetBetSelectctionsKeepWinAction
  | ResetBettingAction
  | SetBetStateAction
  | SetGroupBetTicketStatusClearAll
  | SetPickLegAction
  | SetPickAction
  | SetGroupBetAddProcessing
  | SetGroupBetAddSuccess
  | SetGroupBetAddError
  | SetGroupBetRemoveProcessing
  | SetGroupBetRemoveSuccess
  | SetGroupBetRemoveError
  | SetBetSlipOpen;

export const initialState = {
  config: {
    isProcessing: false,
    isBetPlaced: false,
    isBettingError: false,
    errorBetMessage: "",
    betConfirmationTitle: "",
    repeatBetNumber: 1,
    randomTalentPhrase: "",
    randomTalentPhraseId: -1
  },
  betGroupStatus: {},
  processingBets: 0,
  repeatBetNumber: 1,
  showRepeatBet: false,
  forceStop: false,
  errorBets: 0,
  successBets: 0,
  // URP
  betSelections: [[]],
  scratchedSelections: [[]],
  startBetting: false,
  betState: "none",
  betAmount: 0,
  betType: null,
  betLeg: 0,
  talentPick: null,
  raceOfBet: "",
  betSlipOpen: false
};

const resetArray = (matrix: string[][]): string[][] => matrix.map(() => []);

const keepWinResetArray = (
  selections: string[][],
  isScratchedSelections: boolean,
  columnCount?: number
): string[][] => {
  const matrix = isScratchedSelections ? [[]] : [selections[0]];

  if (columnCount) {
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i < columnCount; i++) {
      matrix.push([]);
    }
  }
  return matrix;
};

export default function reducer(
  state: State = initialState,
  action: BetConfirmationAction
): State {
  switch (action.type) {
    case "SET_BET_TICKET_STATE": {
      return {
        ...state,
        config: {
          ...state.config,
          ...action.payload
        }
      };
    }
    case "SET_GROUP_BET_TICKET_STATUS": {
      return {
        ...state,
        betGroupStatus: {
          ...state.betGroupStatus,
          ...action.payload.betGroupStatus
        },
        ...(isURP || action.payload.isRepeat
          ? {}
          : {
              processingBets: action.payload.processingBets,
              errorBets: action.payload.errorBets,
              successBets: action.payload.successBets
            })
      };
    }
    case "SET_GROUP_BET_TICKET_COUNTERS_RESET": {
      return {
        ...state,
        processingBets: 0,
        errorBets: 0,
        successBets: 0
      };
    }
    case "SET_GROUP_BET_TICKET_STATUS_CLEAR": {
      return {
        ...state,
        processingBets: 0,
        errorBets: 0,
        successBets: 0,
        ...(isURP
          ? {}
          : {
              forceStop: false,
              betGroupStatus: {}
            })
      };
    }
    case "SET_GROUP_BET_TICKET_STOP": {
      return {
        ...state,
        processingBets: 0,
        forceStop: true
      };
    }
    case "SET_BET_REPEAT_NUMBER": {
      return {
        ...state,
        repeatBetNumber: get(action, "payload")
      };
    }
    case "SET_SHOW_REPEAT_BET": {
      return {
        ...state,
        showRepeatBet: action.payload
      };
    }
    // URP
    case "SET_START_BETTING": {
      return {
        ...state,
        startBetting: action.payload
      };
    }
    case "SET_BET_AMOUNT": {
      return {
        ...state,
        betAmount: action.payload
      };
    }
    case "SET_SELECTION_SCRATCHED": {
      return {
        ...state,
        scratchedSelections: action.payload
      };
    }
    case "SET_BET_TYPE": {
      return {
        ...state,
        // $FlowFixMe
        betType: action.payload
      };
    }
    case "SET_GROUP_BET_ADD_PROCESSING": {
      return {
        ...state,
        processingBets: state.processingBets + 1
      };
    }
    case "SET_GROUP_BET_ADD_SUCCESS": {
      return {
        ...state,
        successBets: state.successBets + 1
      };
    }
    case "SET_GROUP_BET_ADD_ERROR": {
      return {
        ...state,
        errorBets: state.errorBets + 1
      };
    }
    case "SET_GROUP_BET_REMOVE_PROCESSING": {
      return {
        ...state,
        processingBets: state.processingBets - 1
      };
    }
    case "SET_GROUP_BET_REMOVE_SUCCESS": {
      return {
        ...state,
        successBets: state.successBets - 1
      };
    }
    case "SET_GROUP_BET_REMOVE_ERROR": {
      return {
        ...state,
        errorBets: state.errorBets - 1
      };
    }
    case "SET_GROUP_BET_TICKET_STATUS_CLEAR_ALL": {
      return {
        ...state,
        betGroupStatus: {},
        processingBets: 0,
        errorBets: 0,
        successBets: 0
      };
    }
    case "SET_RACE_OF_BET": {
      return {
        ...state,
        raceOfBet: action.payload
      };
    }
    case "SET_BET_SELECTIONS": {
      return {
        ...state,
        betSelections: action.payload
      };
    }
    case "SET_SELECTIONS_MATRIX": {
      return {
        ...state,
        betSelections: action.payload.selections
      };
    }
    case "RESET_BET_SELECTIONS": {
      return {
        ...state,
        betSelections: resetArray(state.betSelections),
        scratchedSelections: resetArray(state.scratchedSelections),
        startBetting: false
      };
    }
    case "RESET_BET_SELECTIONS_KEEP_WIN": {
      return {
        ...state,
        betSelections: keepWinResetArray(
          state.betSelections,
          false,
          action.payload
        ),
        scratchedSelections: keepWinResetArray(
          state.scratchedSelections,
          true,
          action.payload
        )
      };
    }
    case "RESET_BETTING": {
      return {
        ...state,
        betSelections: [[]],
        scratchedSelections: [[]],
        startBetting: false,
        betState: "none",
        betAmount: 0,
        betType: null,
        betLeg: 0,
        talentPick: null,
        raceOfBet: "",
        betSlipOpen: false
      };
    }
    case "SET_BET_STATE": {
      return {
        ...state,
        betState: action.payload
      };
    }
    case "SET_PICK_LEG": {
      return {
        ...state,
        betLeg: action.payload
      };
    }
    case "SET_PICK": {
      return {
        ...state,
        talentPick: action.payload.value
      };
    }
    case "SET_BET_SLIP_OPEN": {
      return {
        ...state,
        betSlipOpen: action.payload
      };
    }
    default: {
      return state;
    }
  }
}
